import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
} from '../../util/types';
import { LISTING_TYPE_LABELS, resolveStatus } from './exportHelpers';
import { isLowStock } from './listingCounters';
import { lifetimeViews } from '../../util/listingViews';

/**
 * The one data contract behind a listing row (P25009-389 BR-3 / AC-3).
 *
 * "List view and grid view show the same information" is an acceptance criterion
 * that cannot be met by carefully writing the same fields twice — the two views
 * had already drifted once, which is why the ticket exists. So both views render
 * from this single derivation and neither reads `listing.attributes` directly.
 * Adding a field means adding it here, and it appears in both.
 *
 * Pure: takes a denormalised own-listing plus the counts the page fetched, and
 * returns plain values. No JSX, no formatting that needs `intl` — the price is
 * handed back as the raw Money object so the component can format it with the
 * marketplace currency rules.
 */

/**
 * The size / fitment line, e.g. "225/45R17 • 5/112 • ET45".
 *
 * Which measurements matter depends on what is being sold: a tyre has no bolt
 * pattern and a rim has no tread depth. Enum values are resolved to their Console
 * labels where a label exists, so the row shows "Sommar" rather than "summer".
 *
 * @param {Object} publicData
 * @param {Array} listingFields config.listing.listingFields
 */
export const buildSpecLine = (publicData = {}, listingFields = []) => {
  const label = field => {
    const value = publicData[field];
    const listingField = (listingFields || []).find(({ key }) => key === field) || {};
    const isEnum =
      listingField.schemaType === 'enum' || listingField.schemaType === 'multi-enum';
    if (!isEnum) return value;
    return listingField.enumOptions?.find(({ option }) => option === value)?.label || value;
  };

  const {
    listingType,
    tireWidthFront,
    profileFront,
    diameter,
    numberOfBolts,
    boltCircle,
    threaddepth,
    condition,
    hubHole,
    etFront,
  } = publicData;

  const parts = [];
  const isWheel = listingType === 'wheel' || listingType === 'business-wheel';
  const isTire = listingType === 'tire' || listingType === 'businesstire';
  const isRim = listingType === 'rim' || listingType === 'businesswheel';

  if (isWheel || isTire) {
    if (tireWidthFront && profileFront && diameter) {
      parts.push(`${label('tireWidthFront')}/${label('profileFront')}R${label('diameter')}`);
    }
  }
  if (isWheel || isRim) {
    if (numberOfBolts && boltCircle) parts.push(`${label('numberOfBolts')}/${label('boltCircle')}`);
  }
  if (isRim) {
    if (hubHole) parts.push(`CB${label('hubHole')}`);
    if (etFront) parts.push(`ET${label('etFront')}`);
  }
  if (isWheel || isTire) {
    if (threaddepth) parts.push(String(label('threaddepth')));
  }
  if (condition) parts.push(String(label('condition')));

  return parts.length > 0 ? parts.join(' • ') : null;
};

/**
 * When was this listing published?
 *
 * Sharetribe exposes `createdAt` on a listing but no `publishedAt`, so "publish
 * date" is the creation date. `publicData.updateDate` is deliberately NOT used as
 * a fallback: it is a millisecond timestamp written on every edit and used for
 * search sorting, so a listing edited today would claim to have been published
 * today. The CSV export shows both, under separate headings.
 */
export const publishDateOf = listing => listing?.attributes?.createdAt || null;

/**
 * Everything a row shows, derived once.
 *
 * @param {Object} params
 * @param {Object} params.listing         denormalised own-listing
 * @param {Array}  params.listingFields   config.listing.listingFields
 * @param {Object} params.unreadMessages  { [listingId]: count }
 * @param {Object} params.newOrders       { [listingId]: count }
 * @param {Object} params.viewsByListing  { [listingId]: 7-day views }
 * @param {Object} params.recordedViewsByListing
 *   { [listingId]: running total, added to the frozen counter } (src/util/listingViews.js)
 */
export const buildListingRow = ({
  listing,
  listingFields = [],
  unreadMessages = {},
  newOrders = {},
  viewsByListing = {},
  recordedViewsByListing = {},
}) => {
  const id = listing?.id?.uuid || null;
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const metadata = attributes.metadata || {};

  const state = attributes.state;
  const stock = listing?.currentStock?.attributes?.quantity;

  /* `viewsByListing` covers the last 7 days and only exists once the view-event
   * log has data. `publicData.totalViews` is the lifetime counter, kept as the
   * secondary figure — BR-5 replaces it in the KPI row, not on the row itself,
   * where "how many people have ever seen this" is still the useful number. */
  const sevenDayViews = id && Object.prototype.hasOwnProperty.call(viewsByListing, id)
    ? viewsByListing[id]
    : null;

  return {
    id,
    title: attributes.title || '',
    /* Item code — the human-facing reference. Sellers quote the tyre id on the
     * phone; the raw uuid is meaningless to them, so it is only the fallback. */
    itemCode: metadata.tireId || publicData.reference || id,
    statusLabel: resolveStatus(state, publicData),
    state,
    isDraft: state === LISTING_STATE_DRAFT,
    isPendingApproval: state === LISTING_STATE_PENDING_APPROVAL,
    isClosed: state === LISTING_STATE_CLOSED,
    isRemoved: publicData.isDeleted === true,
    isHidden: publicData.isHide === true,
    // P25009-485 BR-2 — a hidden trade-in: "Gör till säljannons" replaces "Visa".
    isTradein: publicData.isTradein === true && publicData.isHide === true,
    isReserved: publicData.isReserved === true,
    isBoosted: metadata.isBoosted === true,
    listingType: publicData.listingType || null,
    typeLabel: LISTING_TYPE_LABELS[publicData.listingType] || publicData.listingType || '',
    /* "Sökes" listings are requests to buy, not things for sale. The row hides
     * seller tools like Boosta for them. */
    isWanted: publicData.listingType === 'buying',
    specLine: buildSpecLine(publicData, listingFields),
    publishDate: publishDateOf(listing),
    price: attributes.price || null,
    totalViews: lifetimeViews(publicData, id ? recordedViewsByListing[id] : 0),
    sevenDayViews,
    totalLikes: publicData.totalLikes || 0,
    stock: typeof stock === 'number' ? stock : null,
    tracksStock: typeof stock === 'number',
    isLowStock: isLowStock(stock),
    unreadMessageCount: (id && unreadMessages[id]) || 0,
    newOrderCount: (id && newOrders[id]) || 0,
  };
};
